import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { interviewsApi } from '@/api/su/interviews';
import type {
  ICopyInterviewSlot,
  IInterviewInitialState,
  IInterviewSlot,
  IThunkAPI,
  ITimeSlot,
  IUpcomingInterviewPayload,
  IInterviewerList,
  IUpcomingInterview,
  ICancelInterviewPayload,
  IInterviewDetail,
  IInterviewAttendance,
  IInterviewScorePayload,
  IInterviewNotePayload,
  IInterviewerListPayload,
  IRescheduleInterviewPayload,
  IRescheduleInterviewSPPayload,
  ILxcDateTimePayload,
} from '@/store/types';
import { isFulfilledAction, isPendingAction } from '@/helpers';

export const initialState: IInterviewInitialState = {
  interviewSlots: {} as IInterviewSlot,
  interviewSlotsCopy: {} as IInterviewSlot,
  selectedDate: '',
  interviewSlotsCount: undefined,
  interviewNotes: [],
  selectedInterviewId: 0,
  selectedApplicationStatusId: 6,
  interviewerList: [],
  upcomingInterviewsList: [],
  inReviewList: [],
  isLoading: false,
  interviewDetails: {} as IInterviewDetail,
  interviewAttendance: [],
};

export const getInterviewSlotsByDate = createAsyncThunk<{ result: ITimeSlot[] }, ILxcDateTimePayload, IThunkAPI>(
  'get/slots/date',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.getInterviewSlotsByDate(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getInterviewSlotsCount = createAsyncThunk<
  { result: { count: number; date: string }[] },
  { campaignId: number; dateStart: string; dateEnd: string },
  IThunkAPI
>('get/slots/count', async ({ campaignId, dateStart, dateEnd }, thunkAPI) => {
  try {
    const response = await interviewsApi.getInterviewSlotsCount(campaignId, dateStart, dateEnd);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const saveInterviewSlots = createAsyncThunk<unknown, IInterviewSlot, IThunkAPI>(
  'save/slots',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.saveInterviewSlots(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const copyInterviewSlots = createAsyncThunk<any, ICopyInterviewSlot, IThunkAPI>(
  'copy/slots',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.copyInterviewSlots(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getInterviewNotes = createAsyncThunk<{ interviewId: number; note: string }[], number, IThunkAPI>(
  'get/interview/notes',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.getInterviewNotes(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getUpcomingInterviewList = createAsyncThunk<
  { result: IUpcomingInterview[] },
  IUpcomingInterviewPayload,
  IThunkAPI
>('get/upcominginterview/list', async (payload, thunkAPI) => {
  try {
    const response = await interviewsApi.getUpcomingInterviewList(payload);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getInterviewerList = createAsyncThunk<IInterviewerList[], IInterviewerListPayload, IThunkAPI>(
  'get/interviewer/list',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.getInterviewerList(payload.campaignId, payload.applicationStatusId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getInterviewDetail = createAsyncThunk<{ result: IInterviewDetail }, number, IThunkAPI>(
  'get/interview/detail',
  async (interviewId, thunkAPI) => {
    try {
      const response = await interviewsApi.getInterviewDetail(interviewId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getInterviewAttendance = createAsyncThunk<IInterviewAttendance[], undefined, IThunkAPI>(
  'get/interview/attendance',
  async (_, thunkAPI) => {
    try {
      const response = await interviewsApi.getInterviewAttendance();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const saveInterviewScore = createAsyncThunk<any, IInterviewScorePayload, IThunkAPI>(
  'post/interview/score',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.saveInterviewScore(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const addInterviewNote = createAsyncThunk<{ result: IInterviewDetail }, IInterviewNotePayload, IThunkAPI>(
  'post/interview/score',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.addInterviewNote(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const cancelUpcomingInterview = createAsyncThunk<unknown, ICancelInterviewPayload, IThunkAPI>(
  'cancel/upcominginterview',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.cancelUpcomingInterview(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const rescheduleUpcomingInterview = createAsyncThunk<any, IRescheduleInterviewPayload, IThunkAPI>(
  'reschedule/upcominginterview',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.rescheduleUpcomingInterview(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const rescheduleUpcomingInterviewSP = createAsyncThunk<unknown, IRescheduleInterviewSPPayload, IThunkAPI>(
  'SP/interview/reschedule',
  async (payload, thunkAPI) => {
    try {
      const response = await interviewsApi.rescheduleUpcomingInterviewSP(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getInterviewSummary = createAsyncThunk<any, number, IThunkAPI>(
  'interview/summary',
  async (interviewId, thunkAPI) => {
    try {
      const response = await interviewsApi.getInterviewSummary(interviewId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getShowInterview = createAsyncThunk<boolean, number, IThunkAPI>(
  'interview/showDetails',
  async (interviewId, thunkAPI) => {
    try {
      const response = await interviewsApi.getShowInterviewDetails(interviewId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const interviewSlice = createSlice({
  name: 'interviewSlice',
  initialState,
  reducers: {
    setInterviewSlot(state, action) {
      state.interviewSlots = action.payload;
    },
    setSelectedDate(state, action) {
      state.selectedDate = action.payload;
    },
    setInterviewId(state, action) {
      state.selectedInterviewId = action.payload;
    },
    setApplicationStatusId(state, action) {
      state.selectedApplicationStatusId = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(getInterviewSlotsByDate.fulfilled, (state, action) => {
      state.interviewSlots.timeslots = action.payload.result as ITimeSlot[];
      state.interviewSlots.timeslots = action.payload.result.map(slot => {
        return {
          ...slot,
          action: 0,
        };
      });
      state.interviewSlotsCopy.timeslots = action.payload.result as ITimeSlot[];
      state.interviewSlotsCopy.timeslots = action.payload.result.map(slot => {
        return {
          ...slot,
          action: 0,
        };
      });
    });
    builder.addCase(getInterviewSlotsCount.fulfilled, (state, action) => {
      state.interviewSlotsCount = action.payload.result;
    });
    builder.addCase(getInterviewNotes.fulfilled, (state, action) => {
      state.interviewNotes = action.payload;
    });
    builder.addCase(getInterviewerList.fulfilled, (state, action) => {
      state.interviewerList = action.payload;
    });
    builder.addCase(getInterviewDetail.fulfilled, (state, action) => {
      state.interviewDetails = action.payload.result;
    });
    builder.addCase(getInterviewAttendance.fulfilled, (state, action) => {
      state.interviewAttendance = action.payload;
    });

    builder.addCase(getUpcomingInterviewList.fulfilled, (state, action) => {
      if (action?.meta?.arg?.applicationStatusIds?.[0] === 6) state.upcomingInterviewsList = action.payload.result;
      else if (action?.meta?.arg?.applicationStatusIds?.[0] === 8) state.inReviewList = action.payload.result;
    });
    builder.addMatcher(isPendingAction, (state, action) => {
      if (action.type === 'get/upcominginterview/list/pending') state.isLoading = true;
    });
    builder.addMatcher(isFulfilledAction, (state, action) => {
      if (action.type === 'get/upcominginterview/list/fulfilled') state.isLoading = false;
    });
  },
});

export const { setInterviewSlot, setSelectedDate, setInterviewId, setApplicationStatusId } = interviewSlice.actions;

export default interviewSlice.reducer;
